import {
  updateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatch,
  UpdateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatchResponse,
  UpdateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatchData,
  HTTPValidationError,
  AdherenceCheckUpsertSchema,
} from '@common/config/api/client'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { queryClient } from '@common/config/api/queryClient.ts'
import { queryKeyFactory } from '@features/study_details/Subjects/queryKeyFactory'

type TimePointsDetailsCache = {
  data?: {
    timepoint_submission?: {
      container_submissions?: Array<{
        container_submission_id: string
        criteria_check?: string
        [key: string]: unknown
      }>
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

const failedAdherenceCountsByContainer = (
  body: UpdateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatchData['body'],
): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const container of body || []) {
    const checks = (container as { adherence_checks?: AdherenceCheckUpsertSchema[] | null }).adherence_checks
    if (!checks?.length) continue
    counts.set(container.container_submission_id, checks.filter((check) => check.is_adherent === false).length)
  }
  return counts
}

const applyFailedAdherenceCountsToCache = (
  cache: TimePointsDetailsCache | undefined,
  failedCounts: Map<string, number>,
): TimePointsDetailsCache | undefined => {
  const submissions = cache?.data?.timepoint_submission?.container_submissions
  if (!cache?.data?.timepoint_submission || !submissions?.length || failedCounts.size === 0) {
    return cache
  }

  return {
    ...cache,
    data: {
      ...cache.data,
      timepoint_submission: {
        ...cache.data.timepoint_submission,
        container_submissions: submissions.map((submission) => {
          const failedCount = failedCounts.get(submission.container_submission_id)
          if (failedCount === undefined) return submission
          return {
            ...submission,
            criteria_check: String(failedCount),
          }
        }),
      },
    },
  }
}

export const useUpdateContainerSubmission = () => {
  return useMutation<
    UpdateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatchResponse,
    AxiosError<HTTPValidationError>,
    UpdateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatchData
  >({
    mutationFn: async (query) => {
      const response =
        await updateTimepointContainerSubmissionStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdContainerSubmissionsPatch(
          {
            body: query.body,
            path: query.path,
            throwOnError: true,
          },
        )
      return response.data
    },
    onSuccess: async (_, query) => {
      // Refetch first (deployed BE may still return criteria_check=0 until adherence rows exist).
      await queryClient.invalidateQueries({ queryKey: ['TimePointsDetails', query.path.timepoint_submission_id] })
      void queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
      void queryClient.invalidateQueries({
        queryKey: queryKeyFactory.subjectTimepoints({
          study_id: query.path.study_id,
          subject_id: query.path.subject_id,
        }),
      })

      // Overlay failed counts from CheckStep payload so Criteria adherence is correct after Save and close,
      // even when the API has not persisted study_adherence_check rows yet.
      const failedCounts = failedAdherenceCountsByContainer(query.body)
      if (failedCounts.size === 0) return

      queryClient.setQueryData<TimePointsDetailsCache>(
        ['TimePointsDetails', query.path.timepoint_submission_id],
        (old) => applyFailedAdherenceCountsToCache(old, failedCounts),
      )
    },
  })
}
